import Titulo from '../../components/tipografia/Titulo';
import React, { useCallback } from 'react';
import styles from './page.module.css';
import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import Descricao from '../../components/tipografia/Descricao';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import VBox from '../../components/layout/VBox';
import CampoTexto from '../../components/Formulario/CampoTexto';
import CampoData from '@/components/Formulario/CampoData';
import CampoSeletor from '../../components/Formulario/CampoSeletor';
import Badge from '../../components/layout/Badge';
import Grid, { GridItem } from '../../components/layout/Grid';
import Icon from '@mdi/react';
import {
  mdiLoading,
  mdiMagnify,
  mdiPen,
  mdiPlus,
  mdiMinus,
  mdiTrashCan,
  mdiRepeat,
  mdiCheck,
  mdiScaleBalance,
  mdiImport,
} from '@mdi/js';
import DataTable, { DataTableHeader } from '../../components/layout/DataTable';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import BotaoPerigoso from '../../components/BotaoPerigoso';
import { toast } from 'react-toastify';
import Modal from '../../components/layout/Modal';
import { FormLancamento } from './FormLancamento';
import { ModalTransformarRecorrencia } from './ModalTransformarRecorrencia';
import { ModalImportarLancamentos } from './ModalImportarLancamentos';
import { useDebounce } from '../chat/useDebounce';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';

type ResponseList = {
  status: number;
  ok?: boolean;
  result: any[];
  total: number;
  cursor: string | number | null;
};

const statusLabel: Record<string, string> = {
  pago: 'Pago',
  atrasado: 'Atrasado',
  pendente: 'Pendente',
};

const statusVariant: Record<string, any> = {
  pago: 'success',
  atrasado: 'danger',
  pendente: 'warning',
};

const tipoOptions = [
  { id: 'receita', label: 'Receita' },
  { id: 'despesa', label: 'Despesa' },
  { id: 'saldo', label: 'Saldo' },
];

const mesAtual = () => ({
  dataInicio: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
  dataFim: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
});

// Célula de categoria com edição inline: o hover troca o texto pelo seletor.
// O menu do CampoSeletor renderiza em portal (fora da célula), então o
// fechamento no mouseleave é adiado e cancelado se o ponteiro entrar no
// popup (role=listbox/dialog no body) — sem isso o menu fecha antes do clique.
const CategoriaCell = ({
  row,
  categorias,
  onSelecionar,
}: {
  row: any;
  categorias: any[];
  onSelecionar: (row: any, idCategoria: number | null) => void;
}) => {
  const [editando, setEditando] = React.useState(false);
  const timeoutFechamento = React.useRef<any>(null);

  React.useEffect(() => {
    if (!editando) return;
    const cancelaSeEntrarNoPopup = (e: MouseEvent) => {
      const alvo = e.target as Element;
      if (alvo.closest?.('[role="listbox"], [role="dialog"]')) {
        clearTimeout(timeoutFechamento.current);
      }
    };
    document.addEventListener('mouseover', cancelaSeEntrarNoPopup);
    return () => document.removeEventListener('mouseover', cancelaSeEntrarNoPopup);
  }, [editando]);

  React.useEffect(() => () => clearTimeout(timeoutFechamento.current), []);

  // Saldo não tem categoria (o backend zera id_categoria).
  if (row.tipo === 'saldo') {
    return <div>{row.categoria?.nome ?? '-'}</div>;
  }

  if (!editando) {
    return (
      <div
        style={{ cursor: 'pointer', minHeight: 22 }}
        onMouseEnter={() => {
          clearTimeout(timeoutFechamento.current);
          setEditando(true);
        }}
      >
        {row.categoria?.nome ?? '-'}
      </div>
    );
  }

  return (
    <div
      onMouseLeave={() => {
        clearTimeout(timeoutFechamento.current);
        timeoutFechamento.current = setTimeout(() => setEditando(false), 250);
      }}
    >
      <CampoSeletor
        noSearch
        noBorder
        nullable
        instanceId={`categoria-inline-${row.id}`}
        value={row.id_categoria != null ? String(row.id_categoria) : null}
        onChange={(v: string | null) => {
          setEditando(false);
          onSelecionar(row, v != null ? +v : null);
        }}
        options={categorias
          .filter(c => c.tipo === row.tipo)
          .map(c => ({ id: String(c.id), label: c.nome }))}
        placeholder="(Sem categoria)"
      />
    </div>
  );
};

const LancamentoFinanceiroPage = () => {
  const queryClient = useQueryClient();

  const [editObject, setEditObject] = React.useState<any>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<
    string,
    string
  > | null>(null);
  const [recorrenciaObject, setRecorrenciaObject] = React.useState<any>(null);
  const [showImportar, setShowImportar] = React.useState(false);

  const [search, setSearch] = React.useState<string>('');
  const debouncedSearch = useDebounce(search, 500);

  const [tipo, setTipo] = React.useState<string | null>(null);
  const [idCategoria, setIdCategoria] = React.useState<string | null>(null);
  const [idCentroCusto, setIdCentroCusto] = React.useState<string | null>(
    null,
  );

  const periodoPadrao = mesAtual();
  const [dataInicio, setDataInicio] = React.useState<string>(
    periodoPadrao.dataInicio,
  );
  const [dataFim, setDataFim] = React.useState<string>(periodoPadrao.dataFim);

  const handleMesAtual = () => {
    const { dataInicio, dataFim } = mesAtual();
    setDataInicio(dataInicio);
    setDataFim(dataFim);
  };

  const qCategorias = useQuery(
    ['categoria_financeira/box', tipo],
    async () => {
      const res = await axios.get('/api/categoria_financeira/box', {
        params: tipo ? { tipo } : undefined,
      });
      return res.data;
    },
  );
  const categoriasOptions =
    qCategorias.data?.result?.map((it: any) => ({
      id: String(it.id),
      label: it.nome,
    })) ?? [];

  const qCentrosCusto = useQuery('centro_custo/box', async () => {
    const res = await axios.get('/api/centro_custo/box');
    return res.data;
  });
  const centrosCustoOptions =
    qCentrosCusto.data?.result?.map((it: any) => ({
      id: String(it.id),
      label: it.nome,
    })) ?? [];

  const headers: DataTableHeader[] = [
    {
      label: 'Vencimento',
      width: '110px',
      column: 'data_vencimento',
      render: (row: any) =>
        row.data_vencimento
          ? format(parseISO(row.data_vencimento), 'dd/MM/yyyy')
          : '-',
    },
    {
      label: 'Pagamento▼',
      width: '110px',
      render: (row: any) =>
        row.data_pagamento
          ? format(parseISO(row.data_pagamento), 'dd/MM/yyyy')
          : '-',
    },
    { label: 'Descrição', width: '30%', column: 'descricao' },
    {
      label: 'Categoria',
      width: '15%',
      render: (row: any) => (
        <CategoriaCell
          row={row}
          categorias={qCategorias.data?.result ?? []}
          onSelecionar={(rowSel, idCategoria) =>
            categoriaMutator.mutate({ row: rowSel, id_categoria: idCategoria })
          }
        />
      ),
    },
    {
      label: 'Centro de custo',
      width: '15%',
      render: (row: any) => row.centro_custo?.nome ?? '-',
    },
    {
      label: 'Status',
      width: '100px',
      render: (row: any) => (
        <Badge variant={statusVariant[row.status] ?? 'secondary'}>
          {statusLabel[row.status] ?? row.status}
        </Badge>
      ),
    },
    {
      label: 'Valor',
      width: '130px',
      alignRight: true,
      render: (row: any) => (
        <span
          style={{
            color:
              row.tipo === 'saldo'
                ? 'var(--tc-color-gray-700)'
                : row.tipo === 'receita'
                  ? 'var(--tc-color-success-600)'
                  : 'var(--tc-color-tutorial-600)',
            fontWeight: row.tipo === 'saldo' ? 600 : 500,
          }}
        >
          {row.tipo === 'despesa' ? '- ' : ''}
          {Number(row.valor ?? 0).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          })}
        </span>
      ),
    },
    {
      label: 'Ações',
      width: '140px',
      stickRight: true,
      alignRight: true,
      render: (row: any) => {
        return (
          <HBox gap="8px" style={{ justifyContent: 'flex-end' }}>
            {row.tipo !== 'saldo' && !row.data_pagamento && (
              <Botao
                title="Marcar como pago"
                variant="none-success"
                icon={mdiCheck}
                onClick={() => handleMarcarPago(row)}
              />
            )}
            {row.tipo !== 'saldo' && (
              row.id_recorrencia ? (
                <Icon
                  path={mdiRepeat}
                  size={0.9}
                  color="var(--tc-color-gray-400)"
                  title="Faz parte de uma recorrência"
                />
              ) : (
                <Botao
                  title="Transformar em recorrência"
                  variant="none-secondary"
                  icon={mdiRepeat}
                  onClick={() => setRecorrenciaObject(row)}
                />
              )
            )}
            <Botao
              title="Editar"
              variant="none-primary"
              icon={mdiPen}
              onClick={() => setEditObject(row)}
            />
            <BotaoPerigoso
              buttonProps={{ title: 'Apagar' }}
              variant="none-danger"
              icon={mdiTrashCan}
              onClick={() => handleDelete(row.id)}
            />
          </HBox>
        );
      },
    },
  ];

  const queryList = useInfiniteQuery({
    queryKey: [
      'lancamento_financeiro',
      debouncedSearch,
      dataInicio,
      dataFim,
      tipo,
      idCategoria,
      idCentroCusto,
    ],
    queryFn: async ({ pageParam }) => {
      const filter: any = {};
      if (dataInicio && dataFim) {
        filter.data_vencimento = ['between', [dataInicio, dataFim]];
      } else if (dataInicio) {
        filter.data_vencimento = ['ge', dataInicio];
      } else if (dataFim) {
        filter.data_vencimento = ['le', dataFim];
      }
      if (tipo) {
        filter.tipo = ['eq', tipo];
      }
      if (idCategoria) {
        filter.id_categoria = ['eq', idCategoria];
      }
      if (idCentroCusto) {
        filter.id_centro_custo = ['eq', idCentroCusto];
      }

      const params = new URLSearchParams({
        txt: debouncedSearch,
        c: pageParam,
        fi: JSON.stringify(filter),
      });

      return await axios.get<ResponseList>(
        `/api/lancamento_financeiro?${params.toString()}`,
        {
          withCredentials: true,
        },
      );
    },
    getNextPageParam: lastPage => lastPage.data.cursor ?? undefined,
  });

  const changeMutator = useMutation(
    async () => {
      if (!editObject) return;

      setFieldErrors(null);
      if (editObject.id) {
        return await axios.put(
          `/api/lancamento_financeiro/${editObject.id}`,
          editObject,
          {
            withCredentials: true,
          },
        );
      } else {
        return await axios.post(`/api/lancamento_financeiro`, editObject, {
          withCredentials: true,
        });
      }
    },
    {
      onSuccess: () => {
        setFieldErrors(null);
        queryList.refetch();
        // Invalida sempre (não só quando tipo === 'saldo'): cobre edição que
        // muda o tipo de/para 'saldo', que também afeta o checkpoint mais antigo.
        queryClient.invalidateQueries('lancamento_financeiro/saldo_mais_antigo');
        setEditObject(null);

        if (!editObject.id) {
          toast('Lançamento adicionado com sucesso', {
            toastId: 'add-success',
            position: 'top-center',
            type: 'success',
            autoClose: 5000,
          });
        } else {
          toast('Lançamento alterado com sucesso', {
            toastId: 'edit-success',
            position: 'top-center',
            type: 'success',
            autoClose: 5000,
          });
        }
      },
      onError: (err: any) => {
        if (err.response?.status === 422) {
          setFieldErrors(err.response?.data?.fieldErrors ?? {});
        }
        toast(err.response?.data?.msg ?? 'Erro desconhecido', {
          toastId: 'add-error',
          position: 'top-center',
          type: 'warning',
          autoClose: 5000,
        });
      },
    },
  );

  const deleteMutator = useMutation(
    async (id: number) => {
      return await axios.delete(`/api/lancamento_financeiro/${id}`, {
        withCredentials: true,
      });
    },
    {
      onSuccess: () => {
        toast('Lançamento apagado com sucesso', {
          toastId: 'del-success',
          position: 'top-center',
          type: 'success',
          autoClose: 5000,
        });
        queryList.refetch();
        queryClient.invalidateQueries('lancamento_financeiro/saldo_mais_antigo');
      },
      onError: (err: any) => {
        console.error(err);
        toast('Ocorreu um erro ao tentar apagar o lançamento.', {
          toastId: 'del-error',
          position: 'top-center',
          type: 'warning',
          autoClose: 5000,
        });
      },
      onSettled: () => {
        queryList.refetch();
      },
    },
  );

  const handleChange = useCallback(async () => {
    await changeMutator.mutate();
  }, [changeMutator]);

  const handleDelete = (id: number) => {
    deleteMutator.mutate(id);
  };

  const handleCloseModal = () => {
    setEditObject(null);
    setFieldErrors(null);
  };

  const handleOpenModalNewObject = (tipoNovo: 'receita' | 'despesa' | 'saldo') => {
    setEditObject({
      tipo: tipoNovo,
      data_vencimento: format(new Date(), 'yyyy-MM-dd'),
    });
  };

  const marcarPagoMutator = useMutation(
    async (row: any) => {
      return await axios.put(
        `/api/lancamento_financeiro/${row.id}`,
        { ...row, data_pagamento: format(new Date(), 'yyyy-MM-dd') },
        { withCredentials: true },
      );
    },
    {
      onSuccess: () => {
        toast('Lançamento marcado como pago', {
          toastId: 'marcar-pago-success',
          position: 'top-center',
          type: 'success',
          autoClose: 5000,
        });
        queryList.refetch();
      },
      onError: (err: any) => {
        toast(err.response?.data?.msg ?? 'Erro ao marcar como pago', {
          toastId: 'marcar-pago-error',
          position: 'top-center',
          type: 'warning',
          autoClose: 5000,
        });
      },
    },
  );

  const handleMarcarPago = (row: any) => {
    marcarPagoMutator.mutate(row);
  };

  const categoriaMutator = useMutation(
    async ({ row, id_categoria }: { row: any; id_categoria: number | null }) => {
      return await axios.put(
        `/api/lancamento_financeiro/${row.id}`,
        { ...row, id_categoria },
        { withCredentials: true },
      );
    },
    {
      onSuccess: () => {
        toast('Categoria atualizada', {
          toastId: 'categoria-success',
          position: 'top-center',
          type: 'success',
          autoClose: 3000,
        });
        queryList.refetch();
      },
      onError: (err: any) => {
        toast(err.response?.data?.msg ?? 'Erro ao alterar a categoria', {
          toastId: 'categoria-error',
          position: 'top-center',
          type: 'warning',
          autoClose: 5000,
        });
        queryList.refetch();
      },
    },
  );

  const queryFlatList =
    queryList.data?.pages?.map(p => p.data.result)?.flat() ?? [];
  const total =
    queryList.data?.pages?.[queryList.data.pages.length - 1]?.data?.total ?? 0;
  const editMode = !!editObject?.id && editObject.id > 0;

  return (
    <LayoutUsuario>
      <Modal
        instanceId="modal-lancamento"
        ignoreOverlayClick
        title={
          editMode
            ? 'Editar lançamento'
            : editObject?.tipo === 'receita'
              ? 'Nova receita'
              : editObject?.tipo === 'saldo'
                ? 'Definir saldo'
                : 'Nova despesa'
        }
        show={!!editObject}
        onClose={handleCloseModal}
        onCancel={handleCloseModal}
        onConfirm={handleChange}
      >
        <FormLancamento
          value={editObject as any}
          onChange={setEditObject}
          fieldErrors={fieldErrors}
        />
      </Modal>

      {recorrenciaObject && (
        <ModalTransformarRecorrencia
          lancamento={recorrenciaObject}
          onClose={() => setRecorrenciaObject(null)}
          onSuccess={() => {
            setRecorrenciaObject(null);
            queryList.refetch();
          }}
        />
      )}

      <ModalImportarLancamentos
        show={showImportar}
        onClose={() => setShowImportar(false)}
        onSuccess={() => {
          queryList.refetch();
          queryClient.invalidateQueries('lancamento_financeiro/saldo_mais_antigo');
        }}
      />

      <VBox gap="16px" style={{ height: 'calc(100vh - 32px)' }}>
        {/* Header */}
        <HBox>
          <VBox>
            <Titulo>Lançamentos</Titulo>
            <Descricao>
              Registre aqui as receitas e despesas do seu negócio.
            </Descricao>
          </VBox>
          <HBox gap="12px" className={styles.headerAcoes}>
            <Botao
              variant="outline-secondary"
              icon={mdiImport}
              onClick={() => setShowImportar(true)}
            >
              Importar
            </Botao>
            <Botao
              variant="success"
              icon={mdiPlus}
              onClick={() => handleOpenModalNewObject('receita')}
            >
              Receita
            </Botao>
            <Botao
              variant="tutorial"
              icon={mdiMinus}
              onClick={() => handleOpenModalNewObject('despesa')}
            >
              Despesa
            </Botao>
            <Botao
              variant="outline-secondary"
              icon={mdiScaleBalance}
              onClick={() => handleOpenModalNewObject('saldo')}
            >
              Saldo
            </Botao>
          </HBox>
        </HBox>

        {/* Filtros */}
        <Grid>
          <GridItem sm={12}>
            <CampoTexto
              prepend={
                <Icon
                  path={mdiMagnify}
                  size={1}
                  color="var(--tc-color-gray-500)"
                />
              }
              placeholder="Pesquisar"
              value={search}
              onChange={setSearch}
            />
          </GridItem>
          <GridItem sm={12} md={3} lg={2}>
            <CampoSeletor
              nullable
              noSearch
              label="Tipo"
              placeholder="Todos"
              value={tipo}
              onChange={setTipo}
              options={tipoOptions}
            />
          </GridItem>
          <GridItem sm={12} md={3} lg={2}>
            <CampoSeletor
              nullable
              label="Categoria"
              placeholder="Todas"
              value={idCategoria}
              onChange={setIdCategoria}
              options={categoriasOptions}
            />
          </GridItem>
          <GridItem sm={12} md={3} lg={2}>
            <CampoSeletor
              nullable
              label="Centro de custo"
              placeholder="Todos"
              value={idCentroCusto}
              onChange={setIdCentroCusto}
              options={centrosCustoOptions}
            />
          </GridItem>
          <GridItem sm={6} md={3} lg={2}>
            <CampoData
              label="Período inicial"
              value={dataInicio}
              onChange={setDataInicio}
            />
          </GridItem>
          <GridItem sm={6} md={3} lg={2}>
            <CampoData
              label="Período final"
              value={dataFim}
              onChange={setDataFim}
            />
          </GridItem>
          <GridItem sm={12} md={3} lg={2}>
            <Botao variant="outline-primary" onClick={handleMesAtual}>
              Mês atual
            </Botao>
          </GridItem>
        </Grid>

        {queryList.isFetching && (
          <div>
            Aguarde...{' '}
            <Icon
              path={mdiLoading}
              size="14px"
              color="var(--tc-color-gray-700)"
              spin
            />
          </div>
        )}
        {!queryList.isFetching && (
          <div>
            Exibindo {queryFlatList?.length ?? '0'} de {total ?? '0'}{' '}
            resultados.
          </div>
        )}

        <DataTable
          isLoading={queryList.isFetching}
          cabecalhos={headers}
          linhas={queryFlatList ?? []}
          hasMoreData={queryList.hasNextPage}
          onLoadMore={async () => queryList.fetchNextPage()}
        />
      </VBox>
    </LayoutUsuario>
  );
};

export default LancamentoFinanceiroPage;
